import { ForemanJobDetail } from '@ups/xplat/api/dto';
import {
  httpCacheRequestKey,
  onScopedHttpCachePurge,
  stripHttpCacheIdentityScope,
} from '@ups/xplat/utils';
import { hasUnsyncedLocalWork } from './foreman-job-cache-eviction.utils';

/** The job-list endpoint (`ForemanJobService.jobListApiEndpoint`). */
export const FOREMAN_JOB_LIST_API_ENDPOINT = '/api/foreman/job/list/detail';

/**
 * The key the job list is cached under for the CURRENT identity scope (#33947).
 * Built here, beside the retainer that recognises it, so the two cannot drift.
 */
export function foremanJobsCacheKey(spartaApiUrl: string): string {
  const urlWithParams = `${spartaApiUrl}${FOREMAN_JOB_LIST_API_ENDPOINT}`;
  return httpCacheRequestKey({ method: 'GET', urlWithParams });
}

/**
 * Purge retainer for the Foreman job store (#33948 AC2).
 *
 * When a user's scoped cache is purged (sign-out, or another user signing in),
 * their job list goes EXCEPT jobs holding unsynced local work: unsubmitted
 * hours or equipment usage, or a job the server has never seen. Those survive
 * whole, under the same scoped key, so the owner finds them on their next
 * sign-in and nobody else can read them. A job survives with its crew roster,
 * since the draft hours are keyed to it; that is the price of never destroying
 * field work.
 *
 * Returns `undefined` for any other entry, the whole raw value when it cannot
 * be read (a body we do not understand is not provably free of drafts), and
 * `null` when nothing unsynced remains, which deletes the entry.
 */
export function retainUnsyncedForemanJobsOnPurge(
  cacheKey: string,
  rawValue: string
): string | null | undefined {
  const request = stripHttpCacheIdentityScope(cacheKey);
  if (
    !request.startsWith('GET-') ||
    !request.endsWith(FOREMAN_JOB_LIST_API_ENDPOINT)
  ) {
    return undefined;
  }
  let stored: { body?: unknown; timestamp?: number } | null;
  try {
    stored = JSON.parse(rawValue);
  } catch {
    return rawValue;
  }
  if (stored?.body == null) {
    return null;
  }
  if (!Array.isArray(stored.body)) {
    return rawValue;
  }
  const unsynced = (stored.body as ForemanJobDetail[]).filter(
    hasUnsyncedLocalWork
  );
  return unsynced.length ? JSON.stringify({ ...stored, body: unsynced }) : null;
}

/**
 * Register the retainer. Called at module load by foreman-job-cache.utils.ts (which
 * foreman-job.service.ts imports), not
 * from a constructor: a keychain restore at app start runs the sign-in purge
 * (which purges OTHER users' entries), and it must never run before this is in
 * place or another user's unsubmitted hours would be deleted. Idempotent.
 */
export function registerForemanJobCachePurgeRetainer(): void {
  onScopedHttpCachePurge(retainUnsyncedForemanJobsOnPurge);
}
